import React, {createContext, useEffect, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import {useParams} from "react-router";
import individualService, {INDIVIDUAL_QUERY_KEYS} from "../../../../services/web/individual-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import initialData from "../form/resources/initial-data.js"
import bosService from "../../../../services/web/bos-service.js";
import useIndividualUrlParams from "./hooks/use-individual-url-params.js";
export const IndividualContext = createContext(undefined);

const initialPermission = {
    cortexPermission: false,
    localIntelligencePermission: false,
    boPermission: false,
    encounterPermission: false,
    occPermission: false,
    sosPermission: false,
}

const IndividualProvider = ({children}) => {
    const {cpf} = useParams()
    const [individual, setIndividual] = useState(initialData)
    const [changes, setChanges] = useState([])
    const [individualInfo, setIndividualInfo] = useState({})
    const [attachList, setAttachList] = useState([])
    const [attachesDescription, setAttachesDescription] = useState([]);
    const [permissionsProvider, setPermissions] = useState(initialPermission)
    const [faceFile, setFaceFile] = useState(null)
    const [isCpfValid, setIsCpfValid] = useState(true)

    useIndividualUrlParams(setIndividual, setIsCpfValid);

    const { data: boName } = useQuery({
        queryKey: ['boName'],
        queryFn: () => bosService.getBoName(),
        initialData: 'BO'
    });

    const {data, status, refetch} = useQuery({
        queryFn: () =>  individualService.getByCpf(cpf),
        queryKey: INDIVIDUAL_QUERY_KEYS.detail(cpf),
        enabled: !!cpf
    });

    const {data: cortexPermission, status: cortexPermissionStatus} = useQuery({
        queryFn: individualService.getCortexPermission,
        queryKey: INDIVIDUAL_QUERY_KEYS.cortexPermission,
        enabled: !cpf
    });

    useEffect(() => {
        if (cortexPermissionStatus === 'success') {
            setPermissions(prev => ({...prev, cortexPermission}));
        }
    }, [cortexPermission, cortexPermissionStatus]);

    useEffect(() => {
        if (status === 'success') {
            const {individualModel, occurrences, bos, encounters, sos, userCreated, userUpdated, permissions, monitoringId} = data
            const {boPermission, encounterPermission, occPermission, sosPermission, cortexPermission, localIntelligencePermission, sentryFacePermission, analysesNetworkPermission} = permissions
            const {createdAt, updatedAt} = individualModel

            const normalizedData = projectUtils.normalizeData(individualModel.data)

            setIndividual({
                cpf: data.cpf,
                ...normalizedData
            })

            setIndividualInfo({createdAt, userCreated, updatedAt, userUpdated, occurrences, bos, encounters, sos, monitoringId})

            setAttachList(normalizedData.attachs)

            setPermissions({boPermission, encounterPermission, occPermission, cortexPermission, localIntelligencePermission, sentryFacePermission, analysesNetworkPermission, sosPermission})

            const attachesDescription = normalizedData.attachs.reduce((acc, a) => {
                acc[a.attachId] = a.description ?? null;
                return acc;
            }, {});

            setAttachesDescription(attachesDescription)
        }
    }, [data, status]);

    return (
        <IndividualContext.Provider
            value={{
                individual,
                setIndividual,
                permissions: permissionsProvider,
                changes,
                setChanges,
                cpf,
                individualInfo,
                boName,
                attachList, setAttachList,
                attachesDescription, setAttachesDescription,
                faceFile, setFaceFile,
                isCpfValid, setIsCpfValid, refetch
            }}
        >
            {children}
        </IndividualContext.Provider>
    );
};

export default IndividualProvider;
